import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Utensils } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { CUSTOM_FOOD_QUANTITY_UNITS } from "../lib/v2FoodUtils";
import { createCustomFood } from "../lib/v2Database";
import { createV2CustomFood } from "../lib/v2Storage";

const initialForm = {
  name: "",
  name_ar: "",
  category: "",
  quantity: "",
  quantity_unit: "serving",
  potassium_mg: "",
  phosphorus_mg: "",
  sodium_mg: "",
  protein_g: "",
  icon_key: "custom",
};

export default function V2AddCustomFood() {
  const { lang } = useLang();
  const { isGuest, isRegistered, user } = useV2Session();
  const copy = getV2Copy(lang);
  const foodsCopy = copy.foodsCopy;
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const result =
      isRegistered && user?.id
        ? await createCustomFood(user.id, form).catch(() => ({
            ok: false,
            errors: { form: foodsCopy.saveError },
            food: null,
          }))
        : createV2CustomFood(form, { isGuest });
    if (!result.ok) {
      setErrors(result.errors);
      toast.error(foodsCopy.saveError);
      return;
    }

    toast.success(foodsCopy.saveSuccess);
    navigate("/foods");
  };

  return (
    <section className="mx-auto max-w-5xl space-y-6">
      <header className="space-y-3">
        <Button asChild variant="ghost" className="px-0">
          <Link to="/foods">
            <ArrowLeft className="h-4 w-4" />
            {foodsCopy.backToFoods}
          </Link>
        </Button>
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Utensils className="h-5 w-5" />
          </span>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-primary">{copy.brand}</p>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">{foodsCopy.addTitle}</h1>
            <p className="max-w-3xl text-muted-foreground">{foodsCopy.addDescription}</p>
          </div>
        </div>
      </header>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-bold">{foodsCopy.foodDetails}</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label={foodsCopy.foodName} htmlFor="custom-name" error={errors.name}>
              <Input
                id="custom-name"
                value={form.name}
                onChange={(event) => updateField("name", event.target.value)}
              />
            </Field>
            <Field label={foodsCopy.arabicName} htmlFor="custom-name-ar">
              <Input
                id="custom-name-ar"
                value={form.name_ar}
                onChange={(event) => updateField("name_ar", event.target.value)}
              />
            </Field>
            <Field label={foodsCopy.category} htmlFor="custom-category">
              <Input
                id="custom-category"
                value={form.category}
                placeholder={foodsCopy.categoryPlaceholder}
                onChange={(event) => updateField("category", event.target.value)}
              />
            </Field>
            <Field label={foodsCopy.icon} htmlFor="custom-icon">
              <select
                id="custom-icon"
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.icon_key}
                onChange={(event) => updateField("icon_key", event.target.value)}
              >
                <option value="custom">{foodsCopy.iconOptions.custom}</option>
                <option value="fruit">{foodsCopy.iconOptions.fruit}</option>
                <option value="vegetable">{foodsCopy.iconOptions.vegetable}</option>
                <option value="protein">{foodsCopy.iconOptions.protein}</option>
                <option value="grain">{foodsCopy.iconOptions.grain}</option>
                <option value="dairy">{foodsCopy.iconOptions.dairy}</option>
                <option value="meal">{foodsCopy.iconOptions.meal}</option>
              </select>
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-bold">{foodsCopy.servingTitle}</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label={foodsCopy.quantity} htmlFor="custom-quantity" error={errors.quantity}>
              <Input
                id="custom-quantity"
                type="number"
                min="0"
                step="0.01"
                value={form.quantity}
                onChange={(event) => updateField("quantity", event.target.value)}
              />
            </Field>
            <Field label={foodsCopy.quantityUnit} htmlFor="custom-unit" error={errors.quantity_unit}>
              <select
                id="custom-unit"
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.quantity_unit}
                onChange={(event) => updateField("quantity_unit", event.target.value)}
              >
                {CUSTOM_FOOD_QUANTITY_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-bold">{foodsCopy.nutrientsTitle}</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label={foodsCopy.potassiumMg} htmlFor="custom-potassium" error={errors.potassium_mg}>
              <NumberInput id="custom-potassium" value={form.potassium_mg} onChange={(value) => updateField("potassium_mg", value)} />
            </Field>
            <Field label={foodsCopy.phosphorusMg} htmlFor="custom-phosphorus" error={errors.phosphorus_mg}>
              <NumberInput id="custom-phosphorus" value={form.phosphorus_mg} onChange={(value) => updateField("phosphorus_mg", value)} />
            </Field>
            <Field label={foodsCopy.sodiumMg} htmlFor="custom-sodium" error={errors.sodium_mg}>
              <NumberInput id="custom-sodium" value={form.sodium_mg} onChange={(value) => updateField("sodium_mg", value)} />
            </Field>
            <Field label={foodsCopy.proteinG} htmlFor="custom-protein" error={errors.protein_g}>
              <NumberInput id="custom-protein" value={form.protein_g} onChange={(value) => updateField("protein_g", value)} />
            </Field>
          </div>
        </section>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button asChild variant="outline">
            <Link to="/foods">{foodsCopy.cancel}</Link>
          </Button>
          <Button type="submit">
            <Save className="h-4 w-4" />
            {foodsCopy.saveCustomFood}
          </Button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, htmlFor, error, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

function NumberInput({ id, value, onChange }) {
  return (
    <Input
      id={id}
      type="number"
      min="0"
      step="0.01"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
